import React, { useState, useEffect } from "react";
import { CircularProgress, Grid, Typography, Box } from "@mui/material";
import { DateRangePicker } from "rsuite";
import {
  AccountBalanceWallet,
  MonetizationOn,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Restore,
  AttachMoney,
} from "@mui/icons-material";
import MoneyOffCsredIcon from "@mui/icons-material/MoneyOffCsred";
import ErrorIcon from "@mui/icons-material/Error";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import VerifiedIcon from "@mui/icons-material/Verified";
import GppGoodIcon from "@mui/icons-material/GppGood";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import PendingIcon from "@mui/icons-material/Pending";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";

import StatsCard from "../components/StatsCard";
import { getItemWithExpiry } from "../utils";
import { toast } from "react-toastify";
import config from "../config";
import axios from "axios";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "rsuite/dist/rsuite.css";

const PaymentDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalDepositAmount: 0,
    totalSuccessDeposits: 0,
    totalSuccessDepositAmount: 0,
    totalFailedDeposits: 0,
    totalFailedDepositAmount: 0,
    totalPendingDeposits: 0,
    totalPendingDepositAmount: 0,
    totalWithdrawals: 0,
    totalWithdrawalAmount: 0,
    totalSuccessWithdrawals: 0,
    totalSuccessWithdrawalAmount: 0,
    totalPendingWithdrawals: 0,
    totalPendingWithdrawalAmount: 0,
    totalFailedWithdrawals: 0,
    totalFailedWithdrawalAmount: 0,
    totalRefunds: 0,
    totalRefundAmount: 0,
  });

  var date = new Date();
  date.setDate(date.getDate());
  const [dateRange, setDateRange] = useState<[Date, Date]>([date, new Date()]);

  const handleDateSelect = (value: any) => {
    setDateRange(value);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const user = getItemWithExpiry("user");
      if (!user || !user.token) {
        toast.error("You have no access");
        return;
      }

      const startDate = dateRange[0];
      const endDate = dateRange[1];
      const filter = {
        startDate,
        endDate,
      };

      setLoading(true);
      const response = await axios.get(
        `${config.REST_ENDPOINT}/payments/dashboard?filter=${encodeURIComponent(
          JSON.stringify(filter)
        )}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (response && response.data) {
        const { data } = response;
        setStats(data);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [dateRange]);
   if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: "auto",
          marginBottom: "auto",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "20px",
          marginRight: "20px",
        }}
      >
        <p style={{ marginRight: "10px" }}>
          <strong>Select Date Range:</strong>
        </p>
        <DateRangePicker value={dateRange} onChange={handleDateSelect} />
      </div>

      <Grid
        container
        justifyContent="center"
        style={{ marginTop: "10px", marginBottom: "10px" }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">Total Success Deposits</Typography>
            }
            value={stats.totalSuccessDeposits}
            icon={
              <CloudDoneIcon style={{ fontSize: "20px", color: "#4CAF50" }} />
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={<Typography fontWeight="bold">Total Withdrawal</Typography>}
            value={stats.totalWithdrawals}
            icon={
              <AccountBalanceWallet
                style={{ fontSize: "20px", color: "#FF9800" }}
              />
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Success Withdrawals
              </Typography>
            }
            value={stats.totalSuccessWithdrawals}
            icon={
              <CheckCircle style={{ fontSize: "20px", color: "#4CAF50" }} />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Failed Withdrawals
              </Typography>
            }
            value={stats.totalFailedWithdrawals}
            icon={<Cancel style={{ fontSize: "20px", color: "#F44336" }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Pending Withdrawals
              </Typography>
            }
            value={stats.totalPendingWithdrawals}
            icon={
              <PauseCircleFilledIcon
                style={{ fontSize: "20px", color: "#FFC107" }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={<Typography fontWeight="bold">Total Deposits</Typography>}
            value={stats.totalDeposits}
            icon={
              <AddCircleIcon style={{ fontSize: "20px", color: "#4CAF50" }} />
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">Total Failed Deposits</Typography>
            }
            value={stats.totalFailedDeposits}
            icon={<ErrorIcon style={{ fontSize: "20px", color: "#F44336" }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">Total Pending Deposits</Typography>
            }
            value={stats.totalPendingDeposits}
            icon={
              <HourglassEmpty style={{ fontSize: "20px", color: "#FFC107" }} />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={<Typography fontWeight="bold">Total Refunds</Typography>}
            value={stats.totalRefunds}
            icon={<Restore style={{ fontSize: "20px", color: "#9E9E9E" }} />}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Success Deposit Amount
              </Typography>
            }
            value={stats.totalSuccessDepositAmount}
            icon={
              <GppGoodIcon style={{ fontSize: "20px", color: "#4CAF50" }} />
            }
            isAmount={true}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Withdrawals Amount
              </Typography>
            }
            value={stats.totalWithdrawalAmount}
            icon={
              <AccountBalanceWallet
                style={{ fontSize: "20px", color: "#FF9800" }}
              />
            }
            isAmount={true}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">Total Refund Amount</Typography>
            }
            value={stats.totalRefundAmount}
            icon={
              <AttachMoney style={{ fontSize: "20px", color: "#9E9E9E" }} />
            }
            isAmount={true}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Success Withdrawal Amount
              </Typography>
            }
            value={stats.totalSuccessWithdrawalAmount}
            icon={
              <VerifiedIcon style={{ fontSize: "20px", color: "#4CAF50" }} />
            }
            isAmount={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Failed Withdrawal Amount
              </Typography>
            }
            value={stats.totalFailedWithdrawalAmount}
            icon={
              <ReportProblemIcon
                style={{ fontSize: "20px", color: "#F44336" }}
              />
            }
            isAmount={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">Total Deposit Amount</Typography>
            }
            value={stats.totalDepositAmount}
            icon={
              <MonetizationOn style={{ fontSize: "20px", color: "#9C27B0" }} />
            }
            isAmount={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Failed Deposit Amount
              </Typography>
            }
            value={stats.totalFailedDepositAmount}
            icon={
              <MoneyOffCsredIcon
                style={{ fontSize: "20px", color: "#F44336" }}
              />
            }
            isAmount={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Pending Deposit Amount
              </Typography>
            }
            value={stats.totalPendingDepositAmount}
            icon={
              <PendingIcon style={{ fontSize: "20px", color: "#FFC107" }} />
            }
            isAmount={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={
              <Typography fontWeight="bold">
                Total Pending Withdrawal Amount
              </Typography>
            }
            value={stats.totalPendingWithdrawalAmount}
            icon={
              <HourglassEmpty style={{ fontSize: "20px", color: "#FFC107" }} />
            }
            isAmount={true}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default PaymentDashboard;
